import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'

import { Box, Button, ButtonGroup, Grid } from '@mui/material'
import Dialog from '../../../components/Dialog/Dialog'
import { ChainProtocol, OperationType } from '@archax/shared-types'
import { Autocomplete } from '../../../components/ui/Autocomplete'
import { getAllTradersByName } from '../../../api/traders'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Select } from '../../../components/ui/Select'
import { getPayoutGrantableAddresses } from '../../../api/payouts'
import onApiError from '../../../util/on-api-error'
import { createOperationForPayoutContract } from '../../../api/operations'

type GrantKYCFormData = {
  grantee?: { value: string; label: string }
  traderAddressId: string
}

type GrantKYCProps = {
  payoutContractId: string
  protocol: ChainProtocol
  onClose: () => void
  onConfirm: () => void
}
export default function GrantKYCForm({ payoutContractId, onClose, onConfirm }: GrantKYCProps) {
  const { t } = useTranslation()

  const validationSchema = Yup.object()
    .shape({
      traderAddressId: Yup.string().uuid().required(),
    })
    .required()

  const [granteeName, setGranteeName] = useState('')
  const { isLoading: isLoadingTraders, data: tradersData } = useQuery(
    ['get-traders', granteeName],
    () => getAllTradersByName(granteeName),
    {
      retry: false,
    },
  )

  const { mutate: grantKYCMutation } = useMutation(
    (data: { grantee: { value: string }; traderAddressId: string }) =>
      createOperationForPayoutContract(
        OperationType.PayoutContractGrantKyc,
        { traderAddressId: data.traderAddressId },
        {
          payoutContractId,
          traderId: data.grantee.value,
        },
      ),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.pause.successMessage'))
        onConfirm()
      },
      onError: onApiError,
    },
  )

  const onSubmit = (data: GrantKYCFormData) => {
    grantKYCMutation({ grantee: data.grantee as { value: string }, traderAddressId: data.traderAddressId })
  }

  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    trigger,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<GrantKYCFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      grantee: undefined,
      traderAddressId: '',
    },
    resolver: yupResolver(validationSchema),
  })

  const granteeOptions = useMemo(() => {
    const traders = (tradersData as any)?.data?.data || []
    const traderOptions = traders
      .filter((trader: any) => {
        return !trader.deletedAt
      })
      .map((trader: any) => ({
        label: trader.name,
        value: trader.id,
      }))
    return traderOptions
  }, [tradersData, isLoadingTraders])

  const { data: grantableAddresses } = useQuery(
    [`get-payout-grantable-addresses-${payoutContractId}`, watch('grantee')],
    () =>
      (async () => {
        const response = await getPayoutGrantableAddresses(payoutContractId, getValues('grantee')?.value as string)
        return response.data
      })(),
    {
      retry: false,
      enabled: !!getValues('grantee'),
    },
  )

  useEffect(() => {
    if (grantableAddresses?.length) {
      setValue('traderAddressId', grantableAddresses[0].id)
      trigger('traderAddressId')
    }
  }, [grantableAddresses, setValue, trigger, getValues])
  const isSubmitButtonDisabled = !isValid || isSubmitting || isSubmitSuccessful

  return (
    <Dialog title={t('payoutContracts.whitelist.actions.grantKyc.label')} open={true} onClose={onClose}>
      <Grid minWidth={540}>
        <form data-testid="grant-kyc-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ marginBottom: 5 }}>
            <Autocomplete
              name="grantee"
              control={control}
              label={t('payoutContracts.whitelist.actions.grantKyc.selectHolder')}
              options={granteeOptions}
              loadingOptions={isLoadingTraders}
              onInputChange={(inputValue) => {
                setGranteeName(inputValue)
              }}
            />
            <Select
              sx={{ mb: 1 }}
              name="traderAddressId"
              control={control}
              label={t('payoutContracts.whitelist.actions.grantKyc.selectAddress')}
              disabled={!grantableAddresses?.length}
              options={(grantableAddresses || []).map((it: any) => {
                return {
                  value: it.id,
                  label: `${it.addressName}: ${it.address}`,
                }
              })}
            />
          </Box>
          <ButtonGroup fullWidth>
            <Button
              data-testid="grant-kyc-form__submit-button"
              disabled={isSubmitButtonDisabled}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth
            >
              {t('payoutContracts.whitelist.actions.grantKyc.label')}
            </Button>
          </ButtonGroup>
        </form>
      </Grid>
    </Dialog>
  )
}
